import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, LogIn, ArrowRight, Loader, AlertCircle, RefreshCw } from 'lucide-react';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const SignupSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionId] = useState(searchParams?.get('session_id'));
  const [accountStatus, setAccountStatus] = useState('checking'); // checking | ready | creating | error
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      navigate('/sign-up');
      return;
    }
    ensureAccountCreated();
  }, [sessionId]);

  const ensureAccountCreated = async () => {
    setAccountStatus('checking');
    setErrorMessage('');

    try {
      // Call the fallback edge function to ensure account exists
      // (handles both: webhook already ran = idempotent, webhook hasn't run yet = creates account)
      const { data, error } = await supabase?.functions?.invoke('create-account-after-payment', {
        body: { sessionId },
      });

      if (error) {
        throw new Error(error.message || 'Failed to verify account creation');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.success) {
        setAccountStatus('ready');
      } else {
        throw new Error('Unexpected response from account creation service');
      }
    } catch (err) {
      console.error('Account creation check failed:', err);
      setAccountStatus('error');
      setErrorMessage(err?.message || 'Could not verify your account. Please contact support.');
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    ensureAccountCreated();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-success/5 via-background to-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-border text-center">

          {/* Checking / Creating state */}
          {(accountStatus === 'checking' || accountStatus === 'creating') && (
            <>
              <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-full mb-6">
                <Loader className="text-primary animate-spin" size={40} />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Setting Up Your Account
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Payment confirmed! We're creating your account now — this only takes a moment.
              </p>
            </>
          )}

          {/* Error state */}
          {accountStatus === 'error' && (
            <>
              <div className="inline-flex items-center justify-center w-20 h-20 bg-error/10 rounded-full mb-6">
                <AlertCircle className="text-error" size={48} />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Account Setup Issue
              </h1>
              <div className="bg-error/5 border border-error/20 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-error">{errorMessage}</p>
              </div>
              <p className="text-muted-foreground mb-6">
                Your payment was successful. If you're having trouble logging in, please contact us at{' '}
                <a href="mailto:support@aduflow.net" className="text-primary underline">support@aduflow.net</a>{' '}
                and we'll get your account set up right away.
              </p>
              {retryCount < 3 && (
                <Button
                  onClick={handleRetry}
                  className="bg-primary text-white hover:bg-primary/90 flex items-center justify-center gap-2 mx-auto mb-4"
                >
                  <RefreshCw size={18} />
                  Try Again
                </Button>
              )}
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="mx-auto"
              >
                Try Logging In
              </Button>
            </>
          )}

          {/* Success state */}
          {accountStatus === 'ready' && (
            <>
              {/* Success Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-success/10 rounded-full mb-6">
                <CheckCircle className="text-success" size={48} />
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Payment Successful!
              </h1>

              {/* Description */}
              <p className="text-lg text-muted-foreground mb-8">
                Thank you for subscribing to ADU Flow. Your account is ready to use.
              </p>

              {/* Account Ready Notice */}
              <div className="bg-success/5 border border-success/20 rounded-lg p-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <LogIn className="text-success" size={24} />
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold text-foreground mb-2">Your Account Is Ready</h3>
                    <p className="text-sm text-muted-foreground">
                      Your account has been created and confirmed. You can log in immediately using the email address and password you entered during sign-up. No email confirmation is required.
                    </p>
                  </div>
                </div>
              </div>

              {/* What's Next */}
              <div className="text-left mb-8">
                <h3 className="font-semibold text-foreground mb-4">What happens next?</h3>
                <ol className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-primary">1</span>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Log in to your account</div>
                      <div className="text-sm text-muted-foreground">Use the email and password you entered during sign-up</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-primary">2</span>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Set up your organization</div>
                      <div className="text-sm text-muted-foreground">Complete your profile and organization details in Settings</div>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-semibold text-primary">3</span>
                    </div>
                    <div>
                      <div className="font-medium text-foreground">Start managing projects</div>
                      <div className="text-sm text-muted-foreground">Access your dashboard and begin adding projects</div>
                    </div>
                  </li>
                </ol>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/login')}
                  className="bg-primary text-white hover:bg-primary/90 flex items-center justify-center gap-2"
                >
                  Log In Now
                  <ArrowRight size={20} />
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                >
                  Back to Home
                </Button>
              </div>

              {/* Support */}
              <p className="text-sm text-muted-foreground mt-8">
                Need help? Contact our support team at{' '}
                <a href="mailto:support@aduflow.net" className="text-primary underline">support@aduflow.net</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupSuccess;